import { css } from '@emotion/react';
import aria from '@polestar/component-warehouse-react/helpers/accessibility/aria';
import styled from '@emotion/styled';
import * as Icons from '@polestar/component-warehouse-react/tokens/icons/Icons';
import Icon, { IconSizes } from '@polestar/component-warehouse-react/components/icon/Icon';
import { createCSSTransition, MotionDurations, MotionEasings, } from '@polestar/component-warehouse-react/tokens/motion/Motion';
import Spacings, { createCustomSpacing, } from '@polestar/component-warehouse-react/tokens/spacings/Spacings';
import Spacer from '@polestar/component-warehouse-react/components/spacer/Spacer';
import React, { forwardRef, Fragment, useCallback, useMemo, useRef, useState, } from 'react';
import { shouldNotForwardProps } from '@polestar/component-warehouse-react/helpers/shouldForwardProps';
import { minimalHitbox } from '@polestar/component-warehouse-react/helpers/style';
import { useScrollViewEnhanced } from '@polestar/component-warehouse-react/components/scroll-view-enhanced/ScrollViewEnhanced';
import useCombinedRef from '@polestar/component-warehouse-react/hooks/useCombinedRef';
import { m as motion } from 'framer-motion';
import { PrimaryButton } from '@polestar/component-warehouse-react/components/button/Button';
import Orientations from '@polestar/component-warehouse-react/tokens/orientations/Orientations';
import Sizes from '@polestar/component-warehouse-react/tokens/sizes/Sizes';
import { focusProviderOptions } from '@polestar/component-warehouse-react/contexts/focus';
import { useSSRSafeId } from '@polestar/component-warehouse-react/contexts/ssr';
import { useButton } from '@polestar/component-warehouse-react/components/button/Button.helpers';
import { blend } from '@polestar/component-warehouse-react/tokens/colors/Colors';
export var SelectableVariants;
(function (SelectableVariants) {
    SelectableVariants["default"] = "default";
    SelectableVariants["withButton"] = "withButton";
    SelectableVariants["minimal"] = "minimal";
    SelectableVariants["minimalRound"] = "minimalRound";
    SelectableVariants["minimalNumbered"] = "minimalNumbered";
    /**
     * @deprecated use `listIconLeft` or `listIconRight`
     */
    SelectableVariants["list"] = "list";
    SelectableVariants["listIconLeft"] = "listIconLeft";
    SelectableVariants["listIconRight"] = "listIconRight";
    SelectableVariants["listMinimalRound"] = "listMinimalRound";
})(SelectableVariants || (SelectableVariants = {}));
export const selectableVariantsStyles = {
    [Sizes.xSmall]: {
        gap: Spacings.xSmall,
        // TODO: check with design in future - not Spacings size now (42px)
        height: createCustomSpacing(40 / 16),
        width: createCustomSpacing(40 / 16),
        scalePercentage: {
            hover: 0.85,
            checked: 0.76,
        },
    },
    [Sizes.small]: {
        gap: Spacings.medium,
        // TODO: check with design in future - not Spacings size now (48px)
        height: createCustomSpacing(46 / 16),
        width: createCustomSpacing(46 / 16),
        scalePercentage: {
            hover: 0.86,
            checked: 0.76,
        },
    },
    [Sizes.medium]: {
        gap: Spacings.large,
        // TODO: check with design in future - not Spacings size now (72px)
        height: createCustomSpacing(70 / 16),
        width: createCustomSpacing(70 / 16),
        scalePercentage: {
            hover: 0.9,
            checked: 0.84,
        },
    },
    [Sizes.large]: {
        gap: Spacings.large,
    },
};
/*
 * @deprecated only one size is supported now
 * TODO: remove when splitting up selectables
 */
export const selectableVariantsStylesNumbered = {
    [Sizes.xSmall]: {
        height: createCustomSpacing(2.5),
        width: createCustomSpacing(2.5),
    },
    [Sizes.small]: {
        height: createCustomSpacing(2.5),
        width: createCustomSpacing(2.5),
    },
    [Sizes.medium]: {
        height: createCustomSpacing(4.5),
        width: createCustomSpacing(4.5),
    },
};
const Selectable = forwardRef(({ id, children, disabled = false, checked = false, buttonTitle = '', buttonTitleChecked = '', buttonAriaLabel, media, onChange, onClick, onFocus, onBlur, onMouseEnter, onMouseLeave, onTouchStart, onTouchMove, onTouchEnd, name, value, variant = SelectableVariants.default, mediaBorder = true, roundListSize = Sizes.small, minimalRoundSize = Sizes.medium, monochrome = false, ...props }, ref) => {
    const [focused, setFocused] = useState(false);
    const buttonRef = useRef(null);
    const inputRef = useRef(null);
    const combinedRef = useCombinedRef(ref, inputRef);
    const isWithButtonVariant = variant === SelectableVariants.withButton;
    const isListVariant = variant === SelectableVariants.list ||
        variant === SelectableVariants.listIconLeft ||
        variant === SelectableVariants.listIconRight ||
        variant === SelectableVariants.listMinimalRound;
    const isListLeftVariant = variant === SelectableVariants.listIconLeft;
    const isListRightVariant = variant === SelectableVariants.listIconRight || variant === SelectableVariants.list;
    const isMinimalVariant = variant === SelectableVariants.minimal;
    const isMinimalRoundVariant = variant === SelectableVariants.minimalRound;
    const isListMinimalRoundVariant = variant === SelectableVariants.listMinimalRound;
    const isMinimalNumberedVariant = variant === SelectableVariants.minimalNumbered;
    const memoizedId = useSSRSafeId('selectable-field', id);
    const { onSelect } = useScrollViewEnhanced();
    if (variant === SelectableVariants.list) {
        variant = SelectableVariants.listIconRight;
    }
    if (variant === SelectableVariants.minimalNumbered && value && value.toString().length > 2) {
        console.warn('Selectable: no more than 2 digits are allowed in the `minimalNumbered` variant.');
        value = value.toString().slice(0, 2);
    }
    if (isWithButtonVariant && buttonTitle === '') {
        console.warn('Selectable: please provide a "buttonTitle" to put onto the button to ensure it complies with accessibility rules.');
    }
    if (isWithButtonVariant && buttonTitleChecked === '') {
        console.warn('Selectable: please provide a "buttonTitleChecked" to put onto the button to ensure it complies with accessibility rules.');
    }
    const handleClick = useCallback((event) => {
        if (variant !== SelectableVariants.withButton) {
            onChange === null || onChange === void 0 ? void 0 : onChange(!checked, name);
        }
        // prevent that focused line pops up when selecting and then mouseLeave
        setFocused(false);
        if (isMinimalRoundVariant || isMinimalNumberedVariant) {
            onSelect(event.currentTarget);
        }
        onClick === null || onClick === void 0 ? void 0 : onClick(event);
    }, [
        variant,
        isMinimalRoundVariant,
        isMinimalNumberedVariant,
        onClick,
        onChange,
        checked,
        name,
        onSelect,
    ]);
    const handleFocus = useCallback((e) => {
        setFocused(true);
        onFocus === null || onFocus === void 0 ? void 0 : onFocus(e);
    }, [onFocus]);
    const handleBlur = useCallback((e) => {
        setFocused(false);
        onBlur === null || onBlur === void 0 ? void 0 : onBlur(e);
    }, [onBlur]);
    const handleButtonClick = useCallback(() => {
        onChange === null || onChange === void 0 ? void 0 : onChange(!checked, name);
    }, [onChange, name, checked]);
    const ariaSelection = { pressed: checked, role: 'button' };
    if (isMinimalRoundVariant && media === undefined) {
        console.warn('Selectable(Field): please provide a filled "media" property when using the SelectableVariants.listMinimalRound!');
    }
    const { hover, ...buttonEvents } = useButton({
        onMouseEnter,
        onMouseLeave,
        onTouchStart,
        onTouchMove,
        onTouchEnd,
    });
    return (React.createElement(Container, { as: !isWithButtonVariant ? motion.button : motion.div, ...aria({
            disabled: !isWithButtonVariant && disabled,
            ...(!isWithButtonVariant ? ariaSelection : {}),
        }), ...props, variant: variant, onClick: handleClick, ...buttonEvents, onFocus: handleFocus, onBlur: handleBlur, disabled: disabled, minimalRoundSize: minimalRoundSize, ...(!isWithButtonVariant
            ? {
                type: 'button',
                ...focusProviderOptions({
                    round: isMinimalRoundVariant || isMinimalNumberedVariant,
                }),
            }
            : {}) },
        React.createElement(HiddenInput, { ...aria({
                labelledby: `${memoizedId}-content`,
                disabled,
            }), id: memoizedId, ref: combinedRef, readOnly: true, type: "checkbox", disabled: disabled, name: name, value: value, checked: checked, tabIndex: -1 }),
        !isMinimalVariant &&
            !isMinimalRoundVariant &&
            !isMinimalNumberedVariant &&
            !isListVariant && (React.createElement(Border, { checked: checked, hovered: hover, variant: variant, disabled: disabled, monochrome: monochrome })),
        !isListVariant && media && (React.createElement(ImageWrapper, null,
            React.createElement(ImageWrapperInner, { roundVariant: isMinimalRoundVariant, hovered: hover, checked: checked, minimalRoundSize: minimalRoundSize, addInsetAnimation: variant === SelectableVariants.default && !!children },
                React.createElement(ImageContentWrapper, { roundVariant: isMinimalRoundVariant, minimalRoundSize: minimalRoundSize, hovered: hover, checked: checked, disabled: disabled }, media)),
            isMinimalVariant && !isMinimalRoundVariant && (React.createElement(Border, { border: mediaBorder, checked: checked, hovered: hover, disabled: disabled, variant: variant, monochrome: monochrome })),
            isMinimalRoundVariant && (React.createElement(RoundBorder, { checked: checked, hovered: hover, border: mediaBorder, disabled: disabled, monochrome: monochrome })))),
        isMinimalNumberedVariant && (React.createElement(MinimalNumberedWrapper, { id: `${memoizedId}-content`, disabled: disabled, checked: checked, hovered: hover, monochrome: monochrome }, value)),
        !isMinimalRoundVariant && !isMinimalNumberedVariant && (React.createElement(ContentWrapper, { disabled: disabled, border: !isMinimalVariant, checked: checked, hovered: hover, variant: variant, flipDirection: isListMinimalRoundVariant ? Orientations.horizontal : Orientations.vertical },
            isListMinimalRoundVariant && (React.createElement(React.Fragment, null,
                React.createElement(ImageWrapper, { style: {
                        // see "selectableVariantsStyles" above
                        maxWidth: roundListSize === Sizes.xSmall
                            ? createCustomSpacing(40 / 16)
                            : roundListSize === Sizes.small
                                ? createCustomSpacing(46 / 16)
                                : createCustomSpacing(70 / 16),
                        maxHeight: roundListSize === Sizes.xSmall
                            ? createCustomSpacing(40 / 16)
                            : roundListSize === Sizes.small
                                ? createCustomSpacing(46 / 16)
                                : createCustomSpacing(70 / 16),
                    } },
                    React.createElement(ImageWrapperInner, { roundVariant: isListMinimalRoundVariant, hovered: hover, checked: checked, minimalRoundSize: roundListSize, addInsetAnimation: variant === SelectableVariants.default && !!children },
                        React.createElement(ImageContentWrapper, { roundVariant: isListMinimalRoundVariant, hovered: hover, checked: checked, disabled: disabled, minimalRoundSize: roundListSize }, media)),
                    React.createElement(RoundBorder, { checked: checked, hovered: hover, border: mediaBorder, disabled: disabled, monochrome: monochrome })),
                React.createElement(Spacer, { spacing: Spacings.medium, orientation: Orientations.horizontal }))),
            isListLeftVariant && (React.createElement(ListCheckmarkWrapper, null,
                React.createElement(ListCheckmark, { checked: checked, hover: focused || hover, disabled: disabled, "aria-hidden": true, monochrome: monochrome }, checked && (React.createElement(Icon, { pairedWithText: true, icon: Icons.checkmark, size: IconSizes.medium, topOffset: false }))))),
            React.createElement(ChildrenWrapper, { id: `${memoizedId}-content`, variant: variant, disabled: disabled }, children),
            isWithButtonVariant && (React.createElement(Fragment, null,
                React.createElement(Spacer, { spacing: Spacings.large }),
                React.createElement(PrimaryButton, { ref: buttonRef, icon: checked ? Icons.minus : Icons.plus, disabled: disabled, onClick: handleButtonClick, stretch: true, ...aria({
                        label: buttonAriaLabel === null || buttonAriaLabel === void 0 ? void 0 : buttonAriaLabel({ name, checked, ...props }),
                        ...ariaSelection,
                    }) }, checked ? buttonTitleChecked : buttonTitle))),
            isListRightVariant && (React.createElement(ListCheckmarkWrapper, null,
                React.createElement(ListCheckmark, { checked: checked, hover: focused || hover, disabled: disabled, "aria-hidden": true }, checked && (React.createElement(Icon, { pairedWithText: true, icon: Icons.checkmark, size: IconSizes.medium, topOffset: false })))))))));
});
Selectable.displayName = 'Selectable';
export default Selectable;
const HiddenInput = styled.input `
  display: none;
  margin: 0;
  background: red;
`;
const ImageContentWrapper = styled.div(({ theme, roundVariant, hovered, checked, minimalRoundSize, disabled }) => roundVariant
    ? css(css `
          transition: ${createCSSTransition('transform', MotionEasings.easeOut, MotionDurations.normal)};
        `, hovered &&
        css `
            transform: scale(
              ${1 / selectableVariantsStyles[minimalRoundSize].scalePercentage.hover}
            );
          `, checked &&
        css `
            transform: scale(
              ${1 / selectableVariantsStyles[minimalRoundSize].scalePercentage.checked}
            );
          `, disabled &&
        css `
            &:after {
              position: absolute;
              content: '';
              inset-block-start: 0;
              inset-inline-start: 0;
              inset-inline-end: 0;
              inset-block-end: 0;
              background: ${theme.selectable.states.enabled.background};
              opacity: ${theme.selectable.states.disabled.contentOpacity} !important;
            }
          `, roundVariant &&
        disabled &&
        css `
            &:after {
              border-radius: 50%;
            }
          `)
    : disabled &&
        css `
        &:after {
          position: absolute;
          content: '';
          inset-block-start: 0;
          inset-inline-start: 0;
          inset-inline-end: 0;
          inset-block-end: 0;
          background: ${theme.selectable.states.enabled.background};
          opacity: ${theme.selectable.states.disabled.contentOpacity} !important;
        }
      `);
const ImageWrapper = styled.div `
  padding: 0;
  margin: 0;
  width: 100%;
  height: auto;
  overflow: hidden;
  background: transparent;
  position: relative;
  transition: ${createCSSTransition('opacity', MotionEasings.easeOut, MotionDurations.normal)};
`;
const ImageWrapperInner = styled.div(({ theme, roundVariant, hovered, checked, minimalRoundSize, addInsetAnimation }) => {
    const insetDimension = useMemo(() => createCustomSpacing(6 / 16), []);
    return css(css `
      width: 100%;
      height: 100%;
      overflow: hidden;
    `, 
    // mimic hover effect on bottom part of the image to hover up
    addInsetAnimation &&
        css `
        &:after {
          position: absolute;
          content: '';
          inset-block-end: -${insetDimension};
          inset-inline-start: 0;
          inset-inline-end: 0;
          height: ${insetDimension};
          background: ${theme.selectable.states.enabled.background};
          transition: ${createCSSTransition('bottom', MotionEasings.easeOut, MotionDurations.normal)};
        }
      `, addInsetAnimation &&
        hovered &&
        css `
        &:after {
          background: ${theme.selectable.states.hover.background};
        }
      `, addInsetAnimation &&
        hovered &&
        !checked &&
        css `
        &:after {
          inset-block-end: calc(-${insetDimension} * 0.5);
        }
      `, addInsetAnimation &&
        checked &&
        css `
        &:after {
          inset-block-end: 0;
        }
      `, roundVariant &&
        css `
        border-radius: 50%;
        transition: ${createCSSTransition('transform', MotionEasings.easeOut, MotionDurations.normal)};
      `, roundVariant &&
        hovered &&
        css `
        transform: scale(${selectableVariantsStyles[minimalRoundSize].scalePercentage.hover});
      `, roundVariant &&
        checked &&
        css `
        transform: scale(${selectableVariantsStyles[minimalRoundSize].scalePercentage.checked});
      `);
});
// a button or a div wrapper component, but they share the same styling so therefore this approach:
const Container = styled(motion.div, shouldNotForwardProps(false, 'border', 'as', 'focused', 'hovered', 'variant', 'minimalRoundSize'))(({ theme, disabled, variant, minimalRoundSize }) => css(css `
      position: relative;
      padding: 0;
      width: 100%;
      user-select: none;

      /* needed while as='button' is possible, so overwrite 'reset' styles */
      background: transparent;
      text-align: start;
      cursor: default;

      border: none;
      transition: ${createCSSTransition('opacity')},
        ${createCSSTransition('background-color', MotionEasings.easeOut, MotionDurations.normal)};
    `, minimalHitbox, (variant === SelectableVariants.default || variant === SelectableVariants.withButton) &&
    css `
        background-color: ${theme.selectable.states.enabled.background};
      `, !disabled &&
    variant !== SelectableVariants.withButton &&
    css `
        -webkit-tap-highlight-color: transparent;

        &:hover {
          cursor: pointer;
        }
      `, variant === SelectableVariants.minimalRound &&
    css `
        overflow: hidden;
        flex-shrink: 0;
        width: ${selectableVariantsStyles[minimalRoundSize].width};
        height: ${selectableVariantsStyles[minimalRoundSize].height};
        border-radius: 50%;
      `, variant === SelectableVariants.minimalNumbered &&
    css `
        overflow: hidden;
        flex-shrink: 0;
        width: ${createCustomSpacing(2.5)};
        height: ${createCustomSpacing(2.5)};
        border-radius: 50%;
      `));
const RoundBorder = styled.div(({ theme, checked, hovered, border, disabled, monochrome }) => css(css `
      position: absolute;
      width: 100%;
      height: 100%;
      inset-block-start: 0;

      &:before,
      &:after {
        position: absolute;
        display: block;
        content: ' ';
        width: 100%;
        height: 100%;
        inset-block-start: 0;
        inset-inline-start: 0;
        border-radius: 50%;
        opacity: 0;
        will-change: opacity;
        transition: ${createCSSTransition('opacity', MotionEasings.easeOut, MotionDurations.fast)};
      }

      &:before {
        border: ${createCustomSpacing(1 / 16)} solid ${theme.selectable.states.hover.borderColor};
      }

      &:after {
        border: ${createCustomSpacing(2 / 16)} solid
          ${monochrome
    ? theme.selectable.states.active.monochromeBorderColor
    : theme.selectable.states.active.borderColor};
      }
    `, border &&
    css `
        &:before {
          opacity: 1;
        }
      `, hovered &&
    !checked &&
    css `
        &:before {
          opacity: 1;
        }

        &:after {
          opacity: 0;
        }
      `, checked &&
    css `
        &:before {
          opacity: 0;
        }

        &:after {
          opacity: 1;
        }
      `, disabled &&
    checked &&
    css `
        &:before,
        &:after {
          opacity: ${theme.selectable.states.disabled.contentOpacity};
        }
      `));
const Border = styled.div(({ theme, checked, hovered, variant, disabled, border, monochrome }) => {
    const boxShadowInside = { color: undefined, size: undefined };
    const boxShadowBorder = {
        // default state
        color: theme.selectable.states.enabled.borderColor,
        size: createCustomSpacing(1 / 16),
    };
    if (checked) {
        // can be overriden by hover
        boxShadowInside.color = theme.selectable.states.enabled.background;
    }
    if (hovered) {
        boxShadowInside.size = createCustomSpacing(3 / 16);
        boxShadowBorder.size = createCustomSpacing(1 / 16);
        // ensure that the "inside" fill overflows the image content when hovered
        if (variant === SelectableVariants.minimal) {
            // ensure that a more grey color is over the image and inside the border when checked & hovered
            boxShadowInside.color = theme.selectable.states.enabled.background;
            boxShadowBorder.color = theme.selectable.states.enabled.borderColor;
        }
        else if (variant !== SelectableVariants.withButton) {
            boxShadowInside.color = theme.selectable.states.hover.background;
            boxShadowBorder.color = theme.selectable.states.hover.borderColor;
        }
    }
    if (checked) {
        boxShadowInside.size = createCustomSpacing(6 / 16);
        boxShadowBorder.size = createCustomSpacing(2 / 16);
        boxShadowBorder.color = monochrome
            ? theme.selectable.states.active.monochromeBorderColor
            : theme.selectable.states.active.borderColor;
    }
    // Minimal variation border has no border (it's "below" the image)
    if (!border && variant === SelectableVariants.minimal) {
        boxShadowBorder.color = 'transparent';
    }
    // overwrite when disabled
    if (disabled) {
        if (boxShadowInside.color) {
            boxShadowInside.color = blend(theme.selectable.states.enabled.background, boxShadowInside.color, theme.selectable.states.disabled.contentOpacity);
        }
        if (boxShadowBorder.color) {
            boxShadowBorder.color = blend(theme.selectable.states.enabled.background, boxShadowBorder.color, theme.selectable.states.disabled.contentOpacity);
        }
    }
    const boxShadow = boxShadowInside.color !== undefined && boxShadowBorder.color !== undefined
        ? `inset 0 0 0 ${boxShadowBorder.size} ${boxShadowBorder.color}, inset 0 0 0 ${boxShadowInside.size} ${boxShadowInside.color}`
        : `inset 0 0 0 ${boxShadowBorder.size} ${boxShadowBorder.color}`;
    return css(css `
      position: absolute;
      inset-block-start: 0;
      inset-inline-end: 0;
      inset-block-end: 0;
      inset-inline-start: 0;
      pointer-events: none;
      z-index: 1;
      transition: ${createCSSTransition('box-shadow', MotionEasings.easeOut, MotionDurations.normal)};
      will-change: box-shadow;
      box-shadow: ${boxShadow};
    `, 
    // ensure that animation timing will change to faster
    (variant === SelectableVariants.default || variant === SelectableVariants.minimal) &&
        checked &&
        css `
        transition-duration: ${MotionDurations.fast}ms;
      `);
});
const ContentWrapper = styled.div(({ theme, disabled, border, checked, hovered, variant, flipDirection }) => {
    const isList = variant === SelectableVariants.listIconLeft ||
        variant === SelectableVariants.listIconRight ||
        variant === SelectableVariants.listMinimalRound;
    const isDefaultVariant = variant === SelectableVariants.default;
    const isMinimal = variant === SelectableVariants.minimal;
    return css(css `
      width: 100%;
      overflow: hidden;
      color: ${theme.selectable.states.enabled.color};
      transition: ${createCSSTransition('background-color', MotionEasings.easeOut, MotionDurations.normal)},
        ${createCSSTransition('opacity', MotionEasings.easeOut, MotionDurations.normal)};
      &:focus {
        outline: none;
      }
    `, isDefaultVariant &&
        hovered &&
        !checked &&
        css `
        background-color: ${theme.selectable.states.hover.background};
      `, isDefaultVariant &&
        hovered &&
        checked &&
        css `
        background-color: ${theme.selectable.states.hover.background};
      `, isList &&
        css `
        display: flex;
        flex-direction: row;
        align-items: center;
        justify-content: ${variant === SelectableVariants.listIconLeft ? 'start' : 'space-between'};
      `, isMinimal &&
        css `
        padding-block-start: ${Spacings.xSmall};
      `, !isMinimal &&
        !isList &&
        css `
        padding: ${Spacings.medium};
      `, disabled &&
        css `
        pointer-events: none;
      `, !border &&
        !isList &&
        css `
        background-color: transparent;
      `, flipDirection === Orientations.horizontal &&
        css `
        display: flex;
        flex-direction: row;
        align-items: center;
      `);
});
const ChildrenWrapper = styled.div(({ variant, disabled, theme }) => css(variant === SelectableVariants.listIconLeft &&
    css `
          padding-inline-start: ${Spacings.medium};
        `, variant === SelectableVariants.listIconRight &&
    css `
          padding-inline-end: ${Spacings.medium};
        `, (variant === SelectableVariants.listMinimalRound ||
    variant === SelectableVariants.listIconLeft ||
    variant === SelectableVariants.listIconRight) &&
    css `
          flex-grow: 1;
        `, 
// Do not give children opacity in variant with all content inside the border
variant !== SelectableVariants.withButton &&
    disabled &&
    css `
          pointer-events: none;
          opacity: ${theme.selectable.states.disabled.contentOpacity};
        `, 
// this is needed because the content is a slot
variant === SelectableVariants.withButton &&
    disabled &&
    css `
          article,
          ul,
          ol,
          li,
          p,
          h1,
          h2,
          h3,
          h4,
          h5,
          h6 {
            opacity: ${theme.selectable.states.disabled.contentOpacity};
          }
        `));
const ListCheckmarkWrapper = styled.div `
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
`;
const ListCheckmark = styled.div(({ theme, checked, hover, disabled, monochrome }) => {
    return css(css `
      display: flex;
      justify-content: center;
      align-items: center;
      width: ${Spacings.large};
      min-width: ${Spacings.large};
      height: ${Spacings.large};
      min-height: ${Spacings.large};
      border-radius: ${Spacings.medium};
      transition: ${createCSSTransition('all', MotionEasings.easeOut, MotionDurations.fast)};
      transition-property: color, background-color, border-color;
      will-change: color, background-color, border-color;
      background-color: ${theme.selectable.states.enabled.icon.background};
      color: ${theme.selectable.states.enabled.icon.color};
      border: 1px solid ${theme.selectable.states.enabled.icon.border};

      /* Fixes layout shift when checkmark is shown without subtitle */
      & > span {
        position: absolute;
      }
    `, hover &&
        css `
        background-color: ${theme.selectable.states.hover.icon.background};
        color: ${theme.selectable.states.hover.icon.color};
        border-color: ${theme.selectable.states.hover.icon.border};
      `, checked &&
        css `
        background-color: ${monochrome
            ? theme.selectable.states.active.iconSelected.monochrome.background
            : theme.selectable.states.active.iconSelected.background};
        color: ${monochrome
            ? theme.selectable.states.active.iconSelected.monochrome.color
            : theme.selectable.states.active.iconSelected.color};
        border-color: ${monochrome
            ? theme.selectable.states.active.iconSelected.monochrome.border
            : theme.selectable.states.active.iconSelected.border};
      `, disabled &&
        css `
        background-color: ${theme.selectable.states.disabled.iconSelected.background};
        color: ${theme.selectable.states.disabled.iconSelected.color};
        border-color: ${theme.selectable.states.disabled.iconSelected.border};

        &:after {
          content: '';
          width: 100%;
          height: 100%;
          border-radius: 50%;
          background-color: ${checked ? 'none' : theme.selectable.states.disabled.overlay};
          position: absolute;
          inset-block-start: 0;
          inset-inline-start: 0;
        }
      `);
});
const MinimalNumberedWrapper = styled.div(({ theme, disabled, checked, hovered, monochrome }) => {
    return css(css `
      display: flex;
      justify-content: center;
      align-items: center;
      width: 100%;
      height: 100%;
      background-color: ${theme.selectable.states.enabled.minimalNumbered.background};
      color: ${theme.selectable.states.enabled.minimalNumbered.color};
      border-radius: 50%;
      border: ${createCustomSpacing(1 / 16)} solid
        ${theme.selectable.states.enabled.minimalNumbered.border};
      transition: ${createCSSTransition('background-color', MotionEasings.ease, MotionDurations.fast)},
        ${createCSSTransition('border-color', MotionEasings.ease, MotionDurations.fast)},
        ${createCSSTransition('color', MotionEasings.ease, MotionDurations.fast)};
      border-radius: 50%;
      border: ${createCustomSpacing(1 / 16)} solid
        ${theme.selectable.states.enabled.minimalNumbered.border};
      padding-block-start: ${createCustomSpacing(2 / 16)};

      &:focus {
        outline: none;
      }
    `, !checked &&
        hovered &&
        css `
        background-color: ${theme.selectable.states.hover.minimalNumbered.background};
        color: ${theme.selectable.states.hover.minimalNumbered.color};
        border-color: ${theme.selectable.states.hover.minimalNumbered.border};
        border-width: ${createCustomSpacing(1 / 16)};
      `, checked &&
        css `
        background-color: ${theme.selectable.states.active.minimalNumbered.background};
        color: ${theme.selectable.states.active.minimalNumbered.color};
        border-color: ${monochrome
            ? theme.selectable.states.active.minimalNumbered.monochromeBorderColor
            : theme.selectable.states.active.minimalNumbered.border};
        border-width: ${createCustomSpacing(2 / 16)};
      `, disabled &&
        css `
        &:after {
          content: '';
          width: 100%;
          height: 100%;
          border-radius: 50%;
          background-color: ${theme.selectable.states.disabled.overlay};
          position: absolute;
          inset-block-start: 0;
          inset-inline-start: 0;
        }
      `);
});
